// Deposit-zap WIZARD UI — "Fund your HyperEVM wallet" in clear steps.
//
// JP's model (2026-06-13): bridge ONCE to the user's own HyperEVM wallet (USDC +
// a HYPE gas sliver), then deposits into any vault are normal cheap on-chain
// clicks — NOT a bridge per vault. The wizard communicates exactly where the
// user is in the process with honest wait times. No custom contract.

import { quoteZap, executeZap, pollZapStatus, KNOWN_USDC, supportedSourceChain } from './zap.js';
import { HYPEREVM_CHAIN_ID, VAULT_USDC, assessFees } from './zap-core.mjs';
import { getAddress } from './wallet.js';

const USDC_DECIMALS = 6;
// Lowest live vault minimum (Amber $1) — used as the bridge-time vault-min gate so
// nobody bridges then fails the deposit's on-chain minimum. Per-vault override TBD.
const MIN_RECEIVE_USD = 1;
let _state = null; // { fromChainId, fromToken, fromName, amountWei, quote }

function _short(a) { return a ? `${a.slice(0, 6)}…${a.slice(-4)}` : ""; }

// ── X8: persist an in-flight bridge so a refresh / closed tab can resume ──────
// Funds bridge to the user's OWN wallet, so a UI loss never loses money — this is
// purely so the user keeps seeing "your bridge is in progress" across reloads.
const PENDING_KEY = "cc_pending_zap";
const PENDING_TTL_MS = 30 * 60 * 1000; // after this, treat as settled and forget

function _savePending(p) {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify({ ...p, startedAt: p.startedAt || Date.now() })); } catch {}
}
function _loadPending() {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    if (!p || !p.usdcTxHash) return null;
    if (!p.startedAt || Date.now() - p.startedAt > PENDING_TTL_MS) { _clearPending(); return null; }
    return p;
  } catch { return null; }
}
function _clearPending() { try { localStorage.removeItem(PENDING_KEY); } catch {} _removePill(); }

// A small persistent pill on the page so the in-flight bridge survives navigation
// even when the modal is closed. Click → reopen the bridging view.
function _showPill(label = "Bridge in progress") {
  let pill = document.getElementById("zap-pill");
  if (!pill) {
    pill = document.createElement("button");
    pill.id = "zap-pill";
    pill.className = "fixed bottom-4 right-4 z-[90] flex items-center gap-2 px-4 py-2 rounded-full bg-surface-container-high border border-primary/30 text-sm text-on-surface shadow-lg hover:border-primary/60";
    pill.addEventListener("click", () => resumePendingZap());
    document.body.appendChild(pill);
  }
  pill.innerHTML = `<span class="material-symbols-outlined text-primary text-base animate-spin">progress_activity</span><span>${label}</span>`;
}
function _removePill() { document.getElementById("zap-pill")?.remove(); }

// Re-open the modal into the "Bridging…" state and resume polling from the saved
// tx hashes. Exported so the deposit page can call it on load.
export function resumePendingZap() {
  const p = _loadPending();
  if (!p) return;
  _showPill();
  _state = { fromChainId: p.fromChainId, amountWei: String(Math.round((p.expectedUsdc || 0) * 1e6)) };
  _ensureModal().classList.remove("hidden");
  _renderStepper(2);
  _resumeBridging(p);
}

async function _resumeBridging(p) {
  const setProgress = (label, sub = "") => _body(`<div class="text-sm text-on-surface-variant space-y-4 text-center py-4">
    <span class="material-symbols-outlined text-4xl text-primary animate-pulse">sync</span>
    <p class="text-on-surface font-semibold">${label}</p>
    ${sub ? `<p class="text-xs text-on-surface-variant/70">${sub}</p>` : ""}</div>`);
  setProgress("Resuming your bridge…", "We found a transfer still in progress — picking up where it left off.");
  const result = await pollZapStatus({
    usdcTxHash: p.usdcTxHash, gasTxHash: p.gasTxHash, fromChainId: p.fromChainId,
    onStep: (l) => setProgress(l, "Safe to wait — your funds keep moving."),
  });
  if (result.usdc.status === "DONE") { _clearPending(); _onArrived(result); }
  else if (result.usdc.status === "TIMEOUT") _onPendingExit(p.usdcTxHash);
  else { _clearPending(); _onFailed(result.usdc.substatus); }
}

// ── Modal shell (self-injected once) ─────────────────────────────────────────
function _ensureModal() {
  let m = document.getElementById("zap-modal");
  if (m) return m;
  m = document.createElement("div");
  m.id = "zap-modal";
  m.className = "hidden fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm";
  m.innerHTML = `
    <div class="bg-surface-container rounded-xl border border-outline-variant/30 w-full max-w-md p-6 relative">
      <button id="zap-close" class="absolute top-4 right-4 text-on-surface-variant hover:text-on-surface">
        <span class="material-symbols-outlined">close</span>
      </button>
      <h3 class="font-headline font-bold text-lg text-on-surface mb-1 flex items-center gap-2">
        <span class="material-symbols-outlined text-primary">bolt</span>
        Fund your HyperEVM wallet
      </h3>
      <!-- Stepper -->
      <div id="zap-steps" class="flex items-center gap-1.5 my-4 text-[0.6rem] uppercase tracking-wider"></div>
      <div id="zap-body"></div>
    </div>`;
  document.body.appendChild(m);
  m.querySelector("#zap-close").addEventListener("click", closeZapWizard);
  m.addEventListener("click", (e) => { if (e.target === m) closeZapWizard(); });
  return m;
}

function _renderStepper(active) {
  const steps = ["Source", "Review", "Bridging", "Done"];
  const el = document.getElementById("zap-steps");
  if (!el) return;
  el.innerHTML = steps.map((s, i) => {
    const on = i <= active;
    return `<span class="flex items-center gap-1.5">
      <span class="w-5 h-5 rounded-full flex items-center justify-center text-[0.6rem] font-bold ${i === active ? "bg-primary text-on-primary" : on ? "bg-primary/30 text-primary" : "bg-surface-container-high text-on-surface-variant/50"}">${i + 1}</span>
      <span class="${i === active ? "text-primary" : "text-on-surface-variant/50"}">${s}</span>
      ${i < steps.length - 1 ? '<span class="w-4 h-px bg-outline-variant/40 mx-0.5"></span>' : ""}
    </span>`;
  }).join("");
}

function _body(html) { const b = document.getElementById("zap-body"); if (b) b.innerHTML = html; return b; }

export function closeZapWizard() {
  document.getElementById("zap-modal")?.classList.add("hidden");
}

// ── Step 1: where are the funds? ─────────────────────────────────────────────
export async function openZapWizard() {
  const addr = getAddress();
  _ensureModal().classList.remove("hidden");
  _renderStepper(0);

  if (!addr) {
    _body(`<p class="text-sm text-on-surface-variant mb-4">Connect your wallet first, then come back to bridge funds in.</p>
      <button id="zap-connect" class="w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT">Connect Wallet</button>`);
    document.getElementById("zap-connect")?.addEventListener("click", () => { closeZapWizard(); document.getElementById("connect-btn")?.click(); });
    return;
  }

  let chainId = null;
  try { chainId = parseInt(await window.ethereum.request({ method: "eth_chainId" }), 16); } catch {}

  if (chainId === HYPEREVM_CHAIN_ID) {
    _body(`<div class="text-sm text-on-surface-variant space-y-3">
      <p>You're already on <span class="text-on-surface font-semibold">HyperEVM</span>. No bridge needed — if you hold USDC here you can deposit directly. Low on HYPE for gas? You can swap a few cents of USDC to HYPE from the deposit screen.</p>
      <button id="zap-ok" class="w-full py-3 bg-surface-container-high text-on-surface font-headline rounded-DEFAULT border border-outline-variant/30">Got it</button>
    </div>`);
    document.getElementById("zap-ok")?.addEventListener("click", closeZapWizard);
    return;
  }

  if (!supportedSourceChain(chainId)) {
    _body(`<div class="text-sm text-on-surface-variant space-y-3">
      <p>Your wallet is on chain <span class="text-on-surface font-mono">${chainId ?? "?"}</span>, which we can't auto-bridge from yet. Supported: ${Object.values(KNOWN_USDC).map(c => c.name).join(", ")}.</p>
      <p class="text-on-surface-variant/70">Switch your wallet to one of those (holding USDC) and reopen this, or bridge to HyperEVM manually.</p>
      <button id="zap-ok" class="w-full py-3 bg-surface-container-high text-on-surface font-headline rounded-DEFAULT border border-outline-variant/30">Close</button>
    </div>`);
    document.getElementById("zap-ok")?.addEventListener("click", closeZapWizard);
    return;
  }

  const src = KNOWN_USDC[chainId];
  _state = { fromChainId: chainId, fromToken: src.addr, fromName: src.name, userAddr: addr };
  // Best-effort source USDC balance via the connected (source-chain) wallet.
  let balHuman = null;
  try {
    const data = "0x70a08231" + "000000000000000000000000" + addr.slice(2);
    const res = await window.ethereum.request({ method: "eth_call", params: [{ to: src.addr, data }, "latest"] });
    balHuman = Number(BigInt(res)) / 10 ** USDC_DECIMALS;
  } catch {}

  _body(`<div class="text-sm text-on-surface-variant space-y-3">
    <p>Your wallet is on <span class="text-on-surface font-semibold">${src.name}</span>. Vaults live on <span class="text-on-surface font-semibold">HyperEVM</span>, where you need USDC + a little HYPE for gas. We'll bridge it for you — <span class="text-on-surface">once</span>; after that you can deposit into any vault without bridging again.</p>
    <div>
      <div class="flex justify-between text-xs mb-1"><span>Amount (USDC on ${src.name})</span>${balHuman != null ? `<span>Balance: <span class="text-primary">${balHuman.toFixed(2)}</span></span>` : ""}</div>
      <div class="flex items-center bg-surface-container-high rounded-lg border border-outline-variant/20 overflow-hidden">
        <input id="zap-amount" type="number" placeholder="0.00" class="flex-1 bg-transparent px-4 py-3 text-lg font-headline text-on-surface outline-none" />
        ${balHuman != null ? `<button id="zap-max" class="px-3 py-1 mr-2 text-xs text-primary border border-primary/30 rounded">MAX</button>` : ""}
        <span class="pr-4 text-sm text-secondary">USDC</span>
      </div>
    </div>
    <div id="zap-err" class="hidden text-xs text-error"></div>
    <button id="zap-quote" class="w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT disabled:opacity-40">Get bridge quote</button>
  </div>`);
  if (balHuman != null) document.getElementById("zap-max")?.addEventListener("click", () => { document.getElementById("zap-amount").value = String(balHuman); });
  document.getElementById("zap-quote")?.addEventListener("click", _onQuote);
}

// ── Step 2: review the quote ─────────────────────────────────────────────────
async function _onQuote() {
  const amt = parseFloat(document.getElementById("zap-amount")?.value);
  const errEl = document.getElementById("zap-err");
  if (!amt || amt <= 0) { if (errEl) { errEl.textContent = "Enter an amount."; errEl.classList.remove("hidden"); } return; }
  const btn = document.getElementById("zap-quote");
  if (btn) { btn.disabled = true; btn.textContent = "Finding the cheapest route…"; }

  const amountWei = BigInt(Math.floor(amt * 10 ** USDC_DECIMALS)).toString();
  _state.amountWei = amountWei;
  // Two-route quote: USDC leg (bulk → vault USDC) + gas leg (sliver → native HYPE).
  const res = await quoteZap({
    fromChainId: _state.fromChainId, fromToken: _state.fromToken,
    fromAmountWei: amountWei, userAddr: _state.userAddr, depositUsd: amt,
    minReceiveUsd: MIN_RECEIVE_USD,
  });

  if (!res.ok) {
    // The wrong-token guard / dust / too-small / vault-min / no-route messages.
    const msg = res.reason === "wrong-token"
      ? `We stopped this route — it would deliver a different token than the vault's USDC, which is exactly how funds get lost. Try a different amount or chain.`
      : res.reason === "fees-too-high"
        ? `Bridge fees are too large for $${amt} — bridge a larger amount so fees stay a small fraction.`
        : res.reason === "too-small"
          ? `That's below the minimum — bridge a bit more so it can also cover the ~$0.60 gas top-up.`
          : res.reason === "below-vault-min"
            ? res.message
            : res.message || "No route available right now. Try again shortly.";
    _renderStepper(0);
    _body(`<div class="text-sm space-y-3">
      <div class="p-3 rounded-lg bg-error/10 border border-error/30 text-on-surface-variant"><span class="material-symbols-outlined text-error align-middle text-base">shield</span> ${msg}</div>
      <button id="zap-back" class="w-full py-3 bg-surface-container-high text-on-surface font-headline rounded-DEFAULT border border-outline-variant/30">Back</button>
    </div>`);
    document.getElementById("zap-back")?.addEventListener("click", openZapWizard);
    return;
  }

  _state.zap = res;
  _renderStepper(1);
  const outUsdc = Number(res.usdc.toAmountMin) / 10 ** USDC_DECIMALS;
  const eta = res.usdc.quote.estimate?.executionDuration || 60;
  const fees = res.usdc.fees;
  const hypeOut = res.gas ? Number(res.gas.hypeOut) / 1e18 : 0;
  const receiveLine = res.gasAvailable
    ? `~${outUsdc.toFixed(2)} USDC + ~${hypeOut.toFixed(4)} HYPE for gas`
    : `~${outUsdc.toFixed(2)} USDC`;
  // G3: tiered fee assessment for the transparency line + warn banner.
  const fee = assessFees({ totalCostUsd: fees.totalCostUsd, depositUsd: amt });
  const isEth = _state.fromChainId === 1;
  const costPctClass = fee.tier === "warn" ? "text-secondary" : "text-on-surface-variant/60";
  // Honest degrade banner when gas-on-arrival can't route right now.
  const degradeBanner = res.gasAvailable ? "" : `
    <div class="p-3 rounded-lg bg-secondary/10 border border-secondary/30 text-[0.72rem] text-on-surface-variant leading-relaxed">
      <span class="material-symbols-outlined text-secondary align-middle text-base">info</span>
      Gas-on-arrival is temporarily unavailable, so we'll bridge your USDC only. You'll need a little HYPE to deposit — if you already hold some you're set; otherwise try again shortly.
    </div>`;
  // Warn (but allow) when fees are a meaningful slice of a small deposit.
  const warnBanner = fee.tier === "warn" ? `
    <div class="p-3 rounded-lg bg-secondary/10 border border-secondary/30 text-[0.72rem] text-on-surface-variant leading-relaxed">
      <span class="material-symbols-outlined text-secondary align-middle text-base">savings</span>
      Fees are <span class="text-on-surface">${fee.pct}%</span> of this deposit. Bridge ~$${fee.suggestedMinDeposit}+ to push them under 2%${isEth ? ", or bridge from Arbitrum / Base where gas is cents" : ""}.
    </div>` : "";
  _body(`<div class="text-sm text-on-surface-variant space-y-3">
    <div class="rounded-lg bg-surface-container-high p-4 space-y-2">
      <div class="flex justify-between"><span>You bridge</span><span class="text-on-surface font-semibold">${(Number(_state.amountWei)/1e6).toFixed(2)} USDC <span class="text-on-surface-variant/60">on ${_state.fromName}</span></span></div>
      <div class="flex justify-between"><span>You receive on HyperEVM</span><span class="text-on-surface font-semibold">${receiveLine}</span></div>
      <div class="flex justify-between"><span>Arrives as</span><span class="font-mono text-xs text-tertiary">${_short(VAULT_USDC)} ✓ vault USDC</span></div>
      <div class="flex justify-between"><span>Total cost</span><span class="text-on-surface">~$${fee.totalCostUsd.toFixed(2)} <span class="${costPctClass}">(${fee.pct}%)</span></span></div>
      <div class="flex justify-between"><span>Estimated time</span><span class="text-on-surface">~${eta < 90 ? eta + " sec" : Math.round(eta/60) + " min"}</span></div>
    </div>
    ${degradeBanner}${warnBanner}
    <p class="text-[0.7rem] text-on-surface-variant/70 leading-relaxed">
      ${res.gasAvailable ? `We send ~$0.60 of your funds as a separate top-up to native HYPE so you have gas to deposit (two quick confirmations: funds, then gas). ` : ``}The bridge charges its own fee
      (~$${fees.bridgeFeeUsd.toFixed(2)}) — <span class="text-on-surface-variant">that fee is the bridge's, not CrystalClear's</span> (we take nothing on bridging).
    </p>
    <button id="zap-confirm" class="w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT">${res.gasAvailable ? "Bridge to HyperEVM" : "Bridge USDC anyway"}</button>
    <button id="zap-back" class="w-full py-2 text-on-surface-variant text-xs hover:text-on-surface">Back</button>
  </div>`);
  document.getElementById("zap-back")?.addEventListener("click", openZapWizard);
  document.getElementById("zap-confirm")?.addEventListener("click", _onConfirm);
}

// ── Step 3: execute + poll ───────────────────────────────────────────────────
async function _onConfirm() {
  _renderStepper(2);
  const setProgress = (label, sub = "") => _body(`<div class="text-sm text-on-surface-variant space-y-4 text-center py-4">
    <span class="material-symbols-outlined text-4xl text-primary animate-pulse">sync</span>
    <p class="text-on-surface font-semibold">${label}</p>
    ${sub ? `<p class="text-xs text-on-surface-variant/70">${sub}</p>` : ""}
  </div>`);
  setProgress("Starting…");
  try {
    const z = _state.zap;
    const { usdcTxHash, gasTxHash } = await executeZap({
      usdc: z.usdc, gas: z.gas, fromToken: _state.fromToken, split: z.split,
      onStep: (l) => setProgress(l, "You can keep this open — your funds keep moving even if you navigate away."),
    });
    // X8: persist so a refresh / closed tab can resume tracking this bridge.
    _savePending({ usdcTxHash, gasTxHash, fromChainId: _state.fromChainId, expectedUsdc: Number(_state.amountWei) / 1e6, startedAt: Date.now() });
    _showPill();
    setProgress("Bridging… funds in transit", `Usually under a minute. Your source transaction: ${_short(usdcTxHash)}`);
    const result = await pollZapStatus({
      usdcTxHash, gasTxHash, fromChainId: _state.fromChainId,
      onStep: (l) => setProgress(l, "Safe to wait — bridge continues in the background."),
    });
    if (result.usdc.status === "DONE") _onArrived(result);
    else if (result.usdc.status === "TIMEOUT") _onPendingExit(usdcTxHash);
    else _onFailed(result.usdc.substatus);
  } catch (e) {
    if (_isUserRejection(e)) _onCancelled();
    else _onFailed(e.message);
  }
}

// ── Step 4: arrived → deposit ────────────────────────────────────────────────
function _onArrived(result) {
  _clearPending(); // funds landed — no longer in-flight
  _renderStepper(3);
  const gasDone = result?.gas?.status === "DONE";
  const gasLine = gasDone
    ? `You now hold USDC + a little HYPE for gas in your wallet. You can deposit into ccAmber — or any open vault — right now, no more bridging.`
    : `You now hold USDC in your wallet. Your gas top-up is still arriving (or wasn't available) — if you don't have a little HYPE yet, give it a moment and refresh before depositing.`;
  _body(`<div class="text-sm text-on-surface-variant space-y-3 text-center py-2">
    <span class="material-symbols-outlined text-4xl text-tertiary">check_circle</span>
    <p class="text-on-surface font-semibold">Funds arrived on HyperEVM</p>
    <p>${gasLine}</p>
    <button id="zap-deposit" class="w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT">Deposit now</button>
  </div>`);
  document.getElementById("zap-deposit")?.addEventListener("click", () => {
    closeZapWizard();
    window.dispatchEvent(new Event("wallet-changed")); // refresh balances on the deposit page
    document.getElementById("dep-amount")?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

function _onPendingExit(txHash) {
  _showPill("Still bridging…"); // keep pending so a reload can resume tracking
  _body(`<div class="text-sm text-on-surface-variant space-y-3 text-center py-2">
    <span class="material-symbols-outlined text-4xl text-secondary">hourglass_top</span>
    <p class="text-on-surface font-semibold">Still bridging</p>
    <p>This is taking longer than usual but your funds are safe and in transit (tx ${_short(txHash)}). They'll arrive in your HyperEVM wallet — just refresh the deposit page in a few minutes.</p>
    <button id="zap-ok" class="w-full py-3 bg-surface-container-high text-on-surface font-headline rounded-DEFAULT border border-outline-variant/30">Close</button>
  </div>`);
  document.getElementById("zap-ok")?.addEventListener("click", closeZapWizard);
}

// X1: a wallet-rejection (MetaMask code 4001 / ACTION_REJECTED) is a normal "user
// changed their mind", not a failure — don't show a red error screen for it.
function _isUserRejection(e) {
  return e && (e.code === 4001 || e.code === "ACTION_REJECTED" || /user rejected|user denied|rejected the request/i.test(e.message || ""));
}
function _onCancelled() {
  _clearPending();
  _body(`<div class="text-sm text-on-surface-variant space-y-3 text-center py-2">
    <span class="material-symbols-outlined text-4xl text-on-surface-variant">do_not_disturb_on</span>
    <p class="text-on-surface font-semibold">Cancelled</p>
    <p>No problem — you cancelled in your wallet. If a transaction was already submitted, those funds are safe in your wallet; check the deposit page.</p>
    <button id="zap-retry" class="w-full py-3 bg-surface-container-high text-on-surface font-headline rounded-DEFAULT border border-outline-variant/30">Back</button>
  </div>`);
  document.getElementById("zap-retry")?.addEventListener("click", openZapWizard);
}

function _onFailed(msg) {
  _clearPending(); // failed/rejected — refund on source, nothing to resume
  _body(`<div class="text-sm text-on-surface-variant space-y-3 text-center py-2">
    <span class="material-symbols-outlined text-4xl text-error">error</span>
    <p class="text-on-surface font-semibold">Bridge didn't complete</p>
    <p>${msg || "The bridge failed or was rejected."} If funds left your source wallet, the bridge will refund them on the source chain — they're not lost.</p>
    <button id="zap-retry" class="w-full py-3 bg-surface-container-high text-on-surface font-headline rounded-DEFAULT border border-outline-variant/30">Try again</button>
  </div>`);
  document.getElementById("zap-retry")?.addEventListener("click", openZapWizard);
}
